import { useState, useMemo, useEffect } from 'react'
import {
  RentTimeLine,
  CartPropertyDomesticRentTimeLines,
  CartPropertyDomesticRentTimeLinesParams,
} from '~/__generated__/__types__'
import {
  CartItemsV3Query,
  useCartPropertyDomesticRentTimeLinesQuery,
  useUpdateCartItemMutation,
} from '~/__generated__/app/query/Cart/Cart.graphql'
import { DateTime } from 'luxon'

type CartItems = CartItemsV3Query['cartItemsV3']
export type RentTimeLines = CartPropertyDomesticRentTimeLines['timeLines']

export default function useRentTimeHook(
  items: CartItems,
  setCartItems: (items: CartItems) => void
) {
  const onRentTimeChangeClick = (id: string) => {
    setOpenRent(true)
    setOptionId(id)
    items.forEach(item => {
      if (item.__typename === 'DomesticAccommodationCartItem') {
        const option = item.options.find(option => option.id === id)
        if (option) {
          setRentTimeOption({
            checkInAt: option.checkInAt,
            checkInDate: option.checkInDate,
            checkOutAt: option.checkOutAt,
            checkOutDate: option.checkOutDate,
            ratePlanId: option.ratePlanId,
            ratePlanVersion: option.ratePlanVersion,
            roomTypeId: option.roomTypeId,
            adultPax: option.adults,
            surcharges: option.surcharges,
          })
        }
        return false
      }
    })
  }

  const onRentTimeSelect = (rentTime: RentTimeLine) => {
    const newItems = JSON.parse(JSON.stringify(items))
    for (const item of newItems) {
      if (item.__typename === 'DomesticAccommodationCartItem') {
        const option = item.options.find(option => option.id === optionId)
        if (option) {
          if (option) {
            const format = 'yyyy-MM-dd HH:mm'
            const checkInAt = DateTime.fromISO(`${option.checkInDate}T${rentTime.time}+09:00`)
            const checkOutAt = checkInAt.plus({ minutes: rentTime.useTime })
            const input = {
              optionId: option.id,
              domesticReservation: {
                checkInAt: checkInAt.toFormat(format),
                checkOutAt: checkOutAt.toFormat(format),
              },
            }
            return updateCartItem({ variables: { input } })
              .then(() => {
                setRentTime(rentTime)
                setOpenRent(false)
                option.checkInAt = DateTime.fromFormat(
                  input.domesticReservation.checkInAt,
                  'yyyy-MM-dd HH:mm'
                ).toISO()
                option.checkOutAt = DateTime.fromFormat(
                  input.domesticReservation.checkInAt,
                  'yyyy-MM-dd HH:mm'
                )
                  .plus({ minutes: rentTime.useTime })
                  .toISO()
                setCartItems(newItems)
              })
              .catch(() => {
                // setToastMessage('장바구니 이용시간 업데이트를 실패 했습니다')
              })
          }
        }
      }
    }
  }

  const [optionId, setOptionId] = useState('')
  const [isOpenRent, setOpenRent] = useState(false) // 장바구니 대실 시간 선택 BottomSheet
  const [rentTimeOption, setRentTimeOption] = useState<
    CartPropertyDomesticRentTimeLinesParams | undefined
  >(undefined) // 장바구니에서 업데이트 하고자하는 옵션
  const [rentTime, setRentTime] = useState<RentTimeLine | undefined>(undefined) // 장바구니에서 업데이트 하고자 하는 대실시간
  const [timeLines, setTimeLines] = useState<RentTimeLines>([])
  const { data, loading } = useCartPropertyDomesticRentTimeLinesQuery({
    variables: {
      params: rentTimeOption!,
    },
    skip: !isOpenRent || !rentTimeOption,
  })
  const [updateCartItem] = useUpdateCartItemMutation()

  useEffect(() => {
    setTimeLines(() =>
      isOpenRent ? (data?.cartPropertyDomesticRentTimeLines.timeLines ?? []) : []
    )
  }, [data, isOpenRent])

  const noticeText = useMemo(() => {
    const { maxUseTime, lastCheckInTime } = data?.cartPropertyDomesticRentTimeLines ?? {}
    if (maxUseTime && lastCheckInTime && rentTime && rentTime.useTime !== maxUseTime) {
      // 21:00 체크인 시, 3시간 이용 가능합니다. (24시 대실마감)
      const hours = Math.floor(rentTime.useTime / 60)
      const minutes = rentTime.useTime % 60
      const useTimeText = `${hours}시간 ${minutes ? `${minutes}분` : ''}`

      const message = `대실 마감으로 ${rentTime.time}에 체크인하면 ${useTimeText}만 이용할 수 있어요.`

      return message
    }
    return ''
  }, [data, rentTime])

  useEffect(() => {
    for (const item of items) {
      if (item.__typename === 'DomesticAccommodationCartItem') {
        const option = item.options.find(option => option.id === optionId)
        if (option) {
          const time = DateTime.fromISO(rentTimeOption!.checkInAt).toFormat('HH:mm')
          const timeLine = timeLines.find(timeLine => timeLine.time === time)
          if (timeLine) {
            setRentTime(timeLine)
          }
          break
        }
      }
    }
  }, [timeLines, items])

  return {
    isOpenRent: isOpenRent && !loading,
    setOpenRent,
    rentTime,
    setRentTime,
    rentTimeOption,
    setRentTimeOption,
    timeLines,
    noticeText,
    isLoading: loading,
    onRentTimeSelect,
    onRentTimeChangeClick,
  }
}
